import { useEffect, useRef } from "react";

const STORAGE_KEY = "lastFocusedSelectorV1";

// Small CSS.escape fallback
function cssEscape(s: string) {
  if ((window as any).CSS?.escape) return (window as any).CSS.escape(s);
  return s.replace(/([ #;?%&,.+*~\':"!^$[\]()=>|/@])/g, "\\$1");
}

// Build a selector for element (prefer data-focus-key, then id, otherwise structural path)
function getElementSelector(el: Element | null): string | null {
  if (!el || el === document.body) return null;
  const e = el as HTMLElement;

  // prefer custom key
  const dataKey = e.dataset?.focusKey;
  if (dataKey) return `[data-focus-key="${cssEscape(dataKey)}"]`;

  // prefer id
  if (e.id) return `#${cssEscape(e.id)}`;

  // fallback structural path (tag:nth-child)
  const parts: string[] = [];
  let node: Element | null = e;
  // stop at body to keep path reasonable
  while (node && node !== document.body) {
    const parent = node.parentElement as any;
    if (!parent) {
      parts.unshift(node.nodeName.toLowerCase());
      break;
    }
    const tag = node.nodeName.toLowerCase();
    // compute 1-based index among element children
    const children = Array.from(parent.children).filter(
      (c: any) => c.nodeName === node!.nodeName
    );
    let index = 1;
    if (children.length > 1) {
      index = Array.from(parent.children).indexOf(node) + 1;
    }
    parts.unshift(children.length > 1 ? `${tag}:nth-child(${index})` : tag);
    node = parent;
  }
  return parts.join(" > ");
}

export function usePersistentFocusSave() {
  const isKeyboardFocus = useRef(false);

  // --- detect input source ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Tab" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        isKeyboardFocus.current = true;
      }
    };

    const handlePointer = () => {
      isKeyboardFocus.current = false;
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("mousedown", handlePointer, true);
    window.addEventListener("touchstart", handlePointer, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("mousedown", handlePointer, true);
      window.removeEventListener("touchstart", handlePointer, true);
    };
  }, []);

  useEffect(() => {
    // debug
    console.debug("[persistentFocus] hook mounted");

    // save on focusin (bubbles)
    const onFocusIn = (e: FocusEvent) => {
      if (!isKeyboardFocus.current) return;
      const el = e.target as HTMLElement | null;

      const selector = getElementSelector(el);
      if (selector) {
        try {
          sessionStorage.setItem(STORAGE_KEY, selector);
          console.debug("[persistentFocus] saved selector:", selector);
        } catch (err) {
          console.warn("[persistentFocus] sessionStorage set failed", err);
        }
      } else {
        // nothing savable
        // optional: clear storage
        // sessionStorage.removeItem(STORAGE_KEY);
      }
    };

    document.addEventListener("focusin", onFocusIn, true);
    return () => {
      document.removeEventListener("focusin", onFocusIn, true);
    };
  }, []);
}

export function usePersistentFocusRestore(dependencies: Array<any>) {
  const restoredSelectorRef = useRef<string | null>(null);
  const focusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const selector = sessionStorage.getItem(STORAGE_KEY);
    if (!selector) return;

    restoredSelectorRef.current = selector;
    let attempts = 0;
    const maxAttempts = 40; // 40 * 100ms = 4s
    const intervalMs = 100;

    const tryFocus = () => {
      try {
        const el = document.querySelector(selector) as HTMLElement | null;
        if (el) {
          // focus and store ref
          try {
            el.focus({ preventScroll: true });
          } catch {
            el.focus();
          }
          try {
            el.scrollIntoView({ block: "center" });
          } catch {}
          focusedElementRef.current = el;
          console.debug(
            "[persistentRestore] focused:",
            el,
            "selector:",
            selector
          );
          return true;
        }
      } catch (err) {
        console.warn("[persistentRestore] selector query failed:", err);
      }
      return false;
    };

    // immediate attempt + periodic retries until found or timeout
    if (tryFocus()) {
      // found immediately, proceed to install pointer handlers below
    } else {
      const timer = window.setInterval(() => {
        attempts += 1;
        if (tryFocus() || attempts >= maxAttempts) {
          clearInterval(timer);
        }
      }, intervalMs);

      // cleanup timer on unmount
      return () => {
        clearInterval(timer);
      };
    }

    // install pointer handlers to blur the focused keyboard element
    const onFirstPointer = () => {
      const active = document.activeElement as HTMLElement | null;
      // if active element is the one we restored (or matches selector), blur it
      if (!active) return;
      const sel = restoredSelectorRef.current;
      let match = false;
      try {
        if (sel && active.matches(sel)) match = true;
      } catch {
        // matches may throw for some exotic selectors - fallback to identity
        if (focusedElementRef.current && active === focusedElementRef.current)
          match = true;
      }
      if (match) {
        try {
          active.blur();
          sessionStorage.removeItem(STORAGE_KEY);
          console.debug(
            "[persistentRestore] blurred restored element due to pointer interaction"
          );
        } catch {}
      }
      // after first pointer we don't need this handler anymore
      window.removeEventListener("mousedown", onFirstPointer, true);
      window.removeEventListener("touchstart", onFirstPointer, true);
    };

    window.addEventListener("mousedown", onFirstPointer, true);
    window.addEventListener("touchstart", onFirstPointer, true);

    // cleanup: remove pointer handlers when unmounting
    return () => {
      window.removeEventListener("mousedown", onFirstPointer, true);
      window.removeEventListener("touchstart", onFirstPointer, true);
    };
  }, dependencies);
}
